import { useCallback, useRef, useEffect, useState } from "react";
import * as d3 from "d3";
import ChartTooltip from "./ChartTooltip.jsx";
import { useElementWidth } from "../hooks/useElementWidth.js";
import styles from "./TrendChart.module.css";

const BAR_OPACITY = 0.7;

export default function MonthlyChart({ data }) {
  const svgRef = useRef(null);
  const [measureRef, width] = useElementWidth();
  const [tooltip, setTooltip] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  // The same node is both the d3 target and the measured one.
  const setSvg = useCallback((node) => {
    svgRef.current = node;
    measureRef(node);
  }, [measureRef]);

  useEffect(() => {
    if (!svgRef.current || !data.length) return;

    const el = svgRef.current;
    const height = 200;
    const margin = { top: 16, right: 8, bottom: 28, left: 32 };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;
    // A hidden panel measures 0 and would draw negative geometry. Leave the
    // last good render alone; the observer redraws once it is on screen.
    if (innerW <= 0) return;

    d3.select(el).selectAll("*").remove();

    const svg = d3.select(el)
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("width", "100%")
      .attr("height", height);

    const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

    const x = d3.scaleBand()
      .domain(data.map((d) => d.date))
      .range([0, innerW])
      .padding(0.28);

    const maxCount = d3.max(data, (d) => d.workoutCount) || 1;
    const y = d3.scaleLinear()
      .domain([0, Math.ceil(maxCount * 1.2)])
      .range([innerH, 0])
      .nice();

    g.append("g")
      .selectAll("line")
      .data(y.ticks(4))
      .join("line")
      .attr("x1", 0).attr("x2", innerW)
      .attr("y1", (d) => y(d)).attr("y2", (d) => y(d))
      .attr("stroke", "rgba(38,33,25,0.08)")
      .attr("stroke-width", 1);

    g.append("g")
      .selectAll("text")
      .data(y.ticks(4))
      .join("text")
      .attr("x", -8).attr("y", (d) => y(d) + 4)
      .attr("text-anchor", "end")
      .attr("fill", "var(--muted)")
      .attr("font-family", "var(--font-sans)")
      .attr("font-size", 11)
      .text((d) => d);

    const bars = g.selectAll(".bar")
      .data(data)
      .join("rect")
      .attr("class", "bar")
      .attr("x", (d) => x(d.date))
      .attr("y", innerH)
      .attr("width", x.bandwidth())
      .attr("height", 0)
      .attr("fill", "var(--amber)")
      .attr("fill-opacity", BAR_OPACITY)
      .attr("rx", 3)
      .attr("pointer-events", "none");

    bars.transition()
      .delay((_, i) => i * 25)
      .duration(500)
      .ease(d3.easeCubicOut)
      .attr("y", (d) => y(d.workoutCount))
      .attr("height", (d) => innerH - y(d.workoutCount));

    // Full-height hit bands rather than the bars themselves: the space above a
    // bar was dead, and a month with no workouts has no bar to aim at at all.
    const bandOffset = (x.step() - x.bandwidth()) / 2;
    g.selectAll(".band")
      .data(data)
      .join("rect")
      .attr("class", "band")
      .attr("x", (d) => x(d.date) - bandOffset)
      .attr("y", 0)
      .attr("width", x.step())
      .attr("height", innerH)
      .attr("fill", "transparent")
      .attr("cursor", "crosshair")
      .on("mousemove", function(event, d) {
        bars.attr("fill-opacity", (bar) => (bar.date === d.date ? 1 : BAR_OPACITY));
        setTooltip({ label: d.label, count: d.workoutCount });
        setTooltipPos({ x: event.clientX, y: event.clientY });
      })
      .on("mouseleave", function() {
        bars.attr("fill-opacity", BAR_OPACITY);
        setTooltip(null);
      });

    g.selectAll(".xlabel")
      .data(data)
      .join("text")
      .attr("class", "xlabel")
      .attr("x", (d) => x(d.date) + x.bandwidth() / 2)
      .attr("y", innerH + 18)
      .attr("text-anchor", "middle")
      .attr("fill", "var(--muted)")
      .attr("font-family", "var(--font-sans)")
      .attr("font-size", 11)
      .text((d) => d.label);
  }, [data, width]);

  return (
    <div id="monthly-trend-chart" className={styles.panel}>
      <div className={styles.heading}>
        <span className={styles.label}>Monthly workouts</span>
      </div>
      {data.length === 0 ? (
        <div className={styles.empty}>No data for this period</div>
      ) : (
        <svg ref={setSvg} className={styles.svg} />
      )}
      {tooltip && (
        <ChartTooltip className={styles.tooltip} x={tooltipPos.x} y={tooltipPos.y}>
          <span className={styles.tooltipDate}>{tooltip.label}</span>
          <span className={styles.tooltipValue}>{tooltip.count} workout{tooltip.count !== 1 ? "s" : ""}</span>
        </ChartTooltip>
      )}
    </div>
  );
}
